import { useState, useEffect, useRef } from 'react';
import { Sheet, SheetContent, SheetDescription } from './ui/sheet';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Save, X, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import DocsModal from './DocsModal';
import NotesModal from './NotesModal';
import SendUpdateModal from './SendUpdateModal';
import { toProperCase, toSentenceCase } from '../utils/textFormat';
import {
  LOSS_TYPE_KEYS, LOSS_TYPE_LABELS,
  buildFormDataFromClaim, isValidEmail, isValidPhone,
  parseCurrency, parsePhone,
} from '../utils/claimHelpers';
import ClaimHeader from './claim/ClaimHeader';
import ClaimForm from './claim/ClaimForm';
import ClaimDocuments from './claim/ClaimDocuments';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Orchestrator for the Edit Claim panel.
 * State, save/delete handlers, modal coordination — UI lives in:
 *   • ClaimHeader        — title, tabs, edit-mode action bar
 *   • ClaimForm          — all form-field sections
 *   • ClaimDocuments     — docs checklist + pictures preview
 */
const EditClaimPanel = ({ claim, open, onClose, companies, onSaved, onDeleted, lossType, onSwitchType }) => {
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState('update');
  const [docsOpen, setDocsOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesLossType, setNotesLossType] = useState('');
  const [sendUpdateOpen, setSendUpdateOpen] = useState(false);
  const [companyRecipients, setCompanyRecipients] = useState([]);
  const [formData, setFormData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [policyMatches, setPolicyMatches] = useState({});
  const [showCloseWarning, setShowCloseWarning] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const hoverTimers = useRef({});

  // ── Hydrate form state when the claim changes ────────────────────────────
  useEffect(() => {
    if (!claim) return;
    const data = buildFormDataFromClaim(claim);
    setFormData(data);
    setOriginalData(data);
    setEditMode(false);
    setActiveTab('update');
  }, [claim]);

  // ── Fetch insurance policy match (per loss type) for the claim's company/DoL
  useEffect(() => {
    if (!open || !claim?.company_name || !claim?.date_of_loss) { setPolicyMatches({}); return; }
    const dol = (claim.date_of_loss || '').split('T')[0];
    if (!dol) return;
    const fetchMatches = async () => {
      try {
        const res = await axios.get(`${API}/company-insurance-policies/match`, { params: { company_name: claim.company_name, date_of_loss: dol }, withCredentials: true });
        setPolicyMatches(res.data || {});
      } catch { setPolicyMatches({}); }
    };
    fetchMatches();
  }, [open, claim?.company_name, claim?.date_of_loss]);

  // ── Load company notification recipients (passed to SendUpdateModal)
  useEffect(() => {
    if (!open || !claim?.company_name) { setCompanyRecipients([]); return; }
    const load = async () => {
      try {
        const res = await axios.get(`${API}/companies/${encodeURIComponent(claim.company_name)}`, { withCredentials: true });
        setCompanyRecipients(Array.isArray(res.data?.notification_recipients) ? res.data.notification_recipients : []);
      } catch { setCompanyRecipients([]); }
    };
    load();
  }, [open, claim?.company_name]);

  // ── External refresh signal from DocsModal after uploads
  useEffect(() => {
    const handler = () => onSaved?.();
    window.addEventListener('claim-docs-updated', handler);
    return () => window.removeEventListener('claim-docs-updated', handler);
  }, [onSaved]);

  // ── Field change helpers ────────────────────────────────────────────────
  const PROPER_FIELDS_MAIN = ['drivers_name', 'location'];
  const SENTENCE_FIELDS_MAIN = ['description'];
  const handleChange = (field, value) => {
    setFormData(prev => {
      let v = value;
      if (typeof v === 'string') {
        if (PROPER_FIELDS_MAIN.includes(field)) v = toProperCase(v);
        else if (SENTENCE_FIELDS_MAIN.includes(field)) v = toSentenceCase(v);
      }
      const updated = { ...prev, [field]: v };
      if (field === 'police_involved' && value === 'No') {
        updated.citation = '';
        updated.police_report_no = '';
        updated.police_report_not_ready = false;
      }
      return updated;
    });
  };

  const PROPER_FIELDS_TYPE = ['contact', 'adjusters_name'];
  const handleTypeChange = (typeKey, field, value) => {
    setFormData(prev => {
      let v = value;
      if (field === 'contact_phone' || field === 'adjusters_phone') v = parsePhone(value);
      if (field === 'initial_amount' || field === 'final_amount') v = parseCurrency(value);
      if (typeof v === 'string' && PROPER_FIELDS_TYPE.includes(field)) v = toProperCase(v);
      const td = { ...prev.type_data[typeKey], [field]: v };
      if (field === 'submittal' && v !== 'Pending' && v !== prev.type_data[typeKey].submittal) {
        td.status = 'In Process';
      }
      return { ...prev, type_data: { ...prev.type_data, [typeKey]: td } };
    });
  };

  // ── Save / Cancel / Delete ──────────────────────────────────────────────
  const handleCancel = () => {
    setFormData({ ...originalData });
    setEditMode(false);
  };

  const validateBeforeSave = () => {
    if (formData.fault === 'At Fault' && !formData.driver_charged) {
      toast.error('Required: Driver Charged');
      return false;
    }
    const typesToValidate = lossType ? [lossType] : LOSS_TYPE_KEYS.filter(k => formData[k]);
    for (const tk of typesToValidate) {
      const td = formData.type_data[tk];
      const label = LOSS_TYPE_LABELS[tk];
      if (td.status === 'Closed') {
        if (td.submittal === 'Pending') {
          toast.error(`${label}: Submittal cannot be Pending when Status is Closed`);
          return false;
        }
        if (!td.final_amount || !String(td.final_amount).trim()) {
          toast.error(`${label}: Final Amount is required when Status is Closed`);
          return false;
        }
      }
      if (td.submittal === 'In-House') {
        const missing = [];
        if (!td.other_party?.trim()) missing.push('Other Party');
        if (!td.in_house_claim_no?.trim()) missing.push('In-House Claim #');
        if (!td.contact?.trim()) missing.push('Contact');
        if (!td.contact_phone?.trim()) missing.push('Contact Phone');
        if (!td.contact_email?.trim()) missing.push('Contact Email');
        if (missing.length > 0) { toast.error(`${label}: Required fields: ${missing.join(', ')}`); return false; }
        if (!isValidPhone(td.contact_phone)) { toast.error(`${label}: Contact Phone must be a valid 10-digit number`); return false; }
        if (!isValidEmail(td.contact_email)) { toast.error(`${label}: Contact Email must be a valid email address`); return false; }
      }
      if (td.submittal === 'Insurance') {
        const missing = [];
        if (!td.insurance_company?.trim()) missing.push('Insurance Company');
        if (!td.insurance_claim_no?.trim()) missing.push('Insurance Claim #');
        if (!td.adjuster?.trim()) missing.push('Adjuster');
        if (!td.adjusters_phone?.trim()) missing.push('Adjusters Phone');
        if (!td.adjusters_email?.trim()) missing.push('Adjusters Email');
        if (missing.length > 0) { toast.error(`${label}: Required fields: ${missing.join(', ')}`); return false; }
        if (!isValidPhone(td.adjusters_phone)) { toast.error(`${label}: Adjusters Phone must be a valid 10-digit number`); return false; }
        if (!isValidEmail(td.adjusters_email)) { toast.error(`${label}: Adjusters Email must be a valid email address`); return false; }
      }
    }
    if (formData.police_involved === 'Yes') {
      const missing = [];
      if (!formData.police_report_not_ready && !formData.police_report_no?.trim()) missing.push('Police Report #');
      if (!formData.citation) missing.push('Citation Issued');
      if (missing.length > 0) { toast.error(`Required fields: ${missing.join(', ')}`); return false; }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateBeforeSave()) return;
    setSaving(true);
    try {
      const payload = {
        ...formData,
        police_involved: formData.police_involved === 'Yes' ? true : formData.police_involved === 'No' ? false : null,
        date_of_loss: formData.date_of_loss || null,
      };
      const res = await axios.put(`${API}/claims/${encodeURIComponent(claim.report_no)}`, payload);
      toast.success('Claim updated successfully');
      setEditMode(false);

      let freshClaim = res.data?.claim;
      if (freshClaim && res.data?.old_report_no) {
        freshClaim = { ...freshClaim, old_report_no: res.data.old_report_no };
      }
      if (!freshClaim) {
        try {
          const newRN = res.data?.report_no || claim.report_no;
          const fallback = await axios.get(`${API}/claims/${encodeURIComponent(newRN)}`);
          freshClaim = { ...fallback.data, old_report_no: claim.report_no };
        } catch (_) { /* fallback failed */ }
      }
      if (freshClaim) {
        const data = buildFormDataFromClaim(freshClaim);
        setFormData(data);
        setOriginalData(data);
      }
      onSaved?.(freshClaim || undefined);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update claim');
    } finally {
      setSaving(false);
    }
  };

  // Partial vs full delete (when a single loss type is scoped)
  const deleteIsPartial = (() => {
    if (!claim || !lossType) return false;
    const activeTypes = LOSS_TYPE_KEYS.filter(k => claim?.[k]);
    return activeTypes.length > 1 && activeTypes.includes(lossType);
  })();

  const requestDelete = () => setShowDeleteConfirm(true);

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    try {
      const url = deleteIsPartial
        ? `${API}/claims/${encodeURIComponent(claim.report_no)}?loss_type=${lossType}`
        : `${API}/claims/${encodeURIComponent(claim.report_no)}`;
      const res = await axios.delete(url);
      if (res.data?.partial) toast.success(`${lossType.toUpperCase()} removed from claim`);
      else toast.success('Claim deleted');
      if (onDeleted) onDeleted(claim.report_no, res.data);
      else onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete claim');
    }
  };

  const hasUnsavedChanges = () => {
    if (!editMode) return false;
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  const handleClose = (skipDirtyCheck = false) => {
    if (!skipDirtyCheck && hasUnsavedChanges()) {
      setShowCloseWarning(true);
      return;
    }
    setEditMode(false);
    onClose();
  };

  if (!claim) return null;

  const isWalkthroughActive = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('demo_walkthrough_active') === 'true';
  const sheetModal = isWalkthroughActive ? false : !(docsOpen || notesOpen);

  return (
    <>
      <Sheet open={open} onOpenChange={() => {}} modal={sheetModal}>
        <SheetContent
          side="right"
          className={`w-full sm:max-w-xl overflow-y-auto p-0 transition-opacity ${docsOpen || notesOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
          overlayClassName={docsOpen || notesOpen ? 'opacity-0 pointer-events-none' : ''}
          data-testid="edit-claim-panel"
          hideClose
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <SheetDescription className="sr-only">Claim details</SheetDescription>

          <ClaimHeader
            claim={claim} lossType={lossType} editMode={editMode}
            saving={saving} deleteIsPartial={deleteIsPartial}
            showCloseWarning={showCloseWarning} setShowCloseWarning={setShowCloseWarning}
            onClose={handleClose} onSave={handleSave} onCancel={handleCancel} onRequestDelete={requestDelete}
            activeTab={activeTab} setActiveTab={setActiveTab}
            setEditMode={setEditMode} setDocsOpen={setDocsOpen} setSendUpdateOpen={setSendUpdateOpen}
            setNotesLossType={setNotesLossType} setNotesOpen={setNotesOpen}
          />

          {/* Notes loss-type picker (only when claim has multiple loss types) */}
          {!editMode && activeTab === 'notes-picker' && (
            <div className="px-4 py-4" data-testid="notes-type-picker">
              <p className="text-sm font-semibold text-gray-700 mb-3">Select a loss type:</p>
              <div className="space-y-2">
                {[
                  { key: 'al', label: 'Auto Liability Processing', active: claim?.al },
                  { key: 'pd', label: 'Physical Damage Processing', active: claim?.pd },
                  { key: 'cd', label: 'Cargo Damage Processing', active: claim?.cd },
                  { key: 'ocac', label: 'Occupational Accident Processing', active: claim?.ocac },
                  { key: 'wc', label: 'Workers Compensation Processing', active: claim?.wc },
                ].filter(t => t.active).map(t => (
                  <button key={t.key}
                    className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                    data-testid={`notes-pick-${t.key}`}
                    onClick={() => { setNotesLossType(t.key); setNotesOpen(true); setActiveTab('update'); }}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Form Content */}
          {(editMode || activeTab === 'update') && (
            <div className="px-4 py-3 space-y-2.5">
              <ClaimForm
                formData={formData} originalData={originalData} editMode={editMode} lossType={lossType}
                companies={companies} policyMatches={policyMatches} hoverTimers={hoverTimers}
                handleChange={handleChange} handleTypeChange={handleTypeChange}
              />

              <ClaimDocuments
                claim={claim} formData={formData} editMode={editMode} handleChange={handleChange}
              />

              {/* Bottom action buttons (edit-mode mirror of top bar) */}
              {editMode && (
                <div className="flex items-center justify-center gap-6 pb-4 pt-2 border-t">
                  <button onClick={handleSave} disabled={saving}
                    className="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors disabled:opacity-50"
                    data-testid="edit-save-bottom">
                    <Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={handleCancel}
                    className="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-orange-600 transition-colors"
                    data-testid="edit-cancel-bottom">
                    <X className="w-4 h-4" />Cancel
                  </button>
                  <button onClick={requestDelete}
                    className="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-red-600 transition-colors"
                    data-testid="edit-delete-bottom">
                    <Trash2 className="w-4 h-4" />
                    {deleteIsPartial ? `Remove ${lossType?.toUpperCase()}` : 'Delete'}
                  </button>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      <DocsModal open={docsOpen} onClose={() => setDocsOpen(false)} claim={claim} />
      <SendUpdateModal
        open={sendUpdateOpen}
        claim={claim || {}}
        companyRecipients={companyRecipients}
        onClose={() => setSendUpdateOpen(false)}
      />
      <NotesModal open={notesOpen} onClose={() => { setNotesOpen(false); onClose(); }} claim={claim} lossType={notesLossType}
        onFullInfo={() => { setNotesOpen(false); setActiveTab('update'); setEditMode(false); }}
        onSwitchType={(tk) => onSwitchType?.(tk)} />

      {/* Delete confirmation — uses Radix Dialog (rendered via Portal at body
       *  level) so it stacks above the parent Sheet's overlay. The previous
       *  hand-rolled `<div className="z-[60]">` was trapped inside the page's
       *  stacking context and got covered by the Sheet's z-50 portal overlay,
       *  making the Cancel/Delete buttons unclickable. */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent
          className="sm:max-w-sm bg-white p-5"
          hideClose
          data-testid="delete-confirm-dialog"
        >
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-full bg-red-100 flex items-center justify-center">
              <Trash2 className="w-4 h-4 text-red-600" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-sm font-semibold text-gray-900">
                {deleteIsPartial
                  ? `Remove ${lossType?.toUpperCase()} from this claim?`
                  : 'Delete this claim?'}
              </DialogTitle>
              <DialogDescription className="text-xs text-gray-600 mt-1.5">
                {deleteIsPartial
                  ? `This will remove only the ${lossType?.toUpperCase()} loss type. The claim will remain active for its other loss types. ${lossType?.toUpperCase()} notes will be sent to the Recycle Bin.`
                  : 'The claim will be moved to the Recycle Bin. You can restore it later.'}
              </DialogDescription>
            </div>
          </div>
          <div className="flex gap-2 justify-end mt-5">
            <Button size="sm" variant="outline" onClick={() => setShowDeleteConfirm(false)} className="text-xs h-8" data-testid="delete-confirm-cancel">
              Cancel
            </Button>
            <Button size="sm" variant="destructive" onClick={handleDelete} className="text-xs h-8" data-testid="delete-confirm-ok">
              {deleteIsPartial ? `Remove ${lossType?.toUpperCase()}` : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditClaimPanel;
